import { useState } from 'react';
import { useRouter } from 'next/router';
import { useSWRInfinite } from 'swr';
import fetcher from '$lib/fetcher';
import { IFullSearchResult, ISanitizedFilterOutput } from '$models';
import { isDefined } from '$lib/helpers';
import { getFilterParamsFromResult, getUrlKey, updateQueryParams } from '../../helpers';
import { key } from '~/utils';
import { SearchContentTypes } from '~/modules/search';

/**
 * Hook for fetching filter data, with built-in pagination.
 * @param filterId for category pages this is the `Id` property.
 * @param facetParams All facets to be applied on filter
 * @param initialData Initial data for SSR
 * @returns
 */
export const useSearchData = (
    search: string,
    initialSearchMode: SearchContentTypes = SearchContentTypes.All,
    filterId?: string,
    initialData?: IFullSearchResult[],
    forcedSearchMode?: boolean,
    pid?: string,
    pageId?: string,
    sId?: string,
    userInteracted?: boolean
) => {
    const router = useRouter();

    const [baseState, setBaseState] = useState<{
        mode: SearchContentTypes;
        size: number;
    }>({ mode: initialSearchMode, size: 1 });

    const [facets, setFacets] = useState(
        getFilterParamsFromResult(
            initialData?.length ? initialData?.[initialData.length - 1]?.products?.filterResult : {}
        )
    );

    const { data, error, size, setSize } = useSWRInfinite<IFullSearchResult>(
        (index) =>
            getUrlKey(
                index,
                search,
                baseState.mode || initialSearchMode,
                filterId,
                facets,
                undefined,
                pid,
                pageId,
                sId
            ),
        fetcher,
        { initialData, initialSize: baseState.size || 0, revalidateOnMount: false }
    );

    // Load next page can either set filter or simply fetch next page with current filter.
    // Logic needed to accommodate for adapting search result page.
    const loadNextPage = (newMode?: SearchContentTypes) => {
        if (newMode && newMode !== baseState.mode) {
            setBaseState({ mode: newMode, size: 2 });
        } else {
            setSize((prev) => prev + 1);
        }
    };

    // Use last data entry for meta data.
    const lastEntry = data?.[data.length - 1];
    const lastProductEntry = lastEntry?.products?.filterResult;

    const loading = !data && !error;

    const productResults: ISanitizedFilterOutput[] | undefined = !data?.some(isDefined)
        ? undefined
        : data?.map((d) => d?.products?.filterResult).filter(isDefined);

    const authors = data
        ?.map((d) => d.authors)
        ?.reduce((acc, cur) => ({
            ...acc,
            content: [...(acc?.authors || []), ...(cur?.authors || [])],
        }));

    const articles = data
        ?.map((d) => d.articles)
        ?.reduce((acc, cur) => ({
            ...acc,
            content: [...(acc?.content || []), ...(cur?.content || [])],
        }));

    const categories = data
        ?.map((d) => d.categories)
        ?.reduce((acc, cur) => ({
            ...acc,
            content: [...(acc?.content || []), ...(cur?.content || [])],
        }));

    const series = data
        ?.map((d) => d.series)
        ?.reduce((acc, cur) => ({
            ...acc,
            series: [...(acc?.series || []), ...(cur?.series || [])],
        }));

    // Calculate cummulative search result count.
    const totalSearchResults =
        (articles?.totalDocumentsFound || 0) +
        (authors?.totalDocumentsFound || 0) +
        (categories?.totalDocumentsFound || 0) +
        (series?.totalDocumentsFound || 0) +
        (lastProductEntry?.totalDocumentsFound || 0);

    updateQueryParams(router, baseState.mode, lastEntry, forcedSearchMode, userInteracted);

    const dataKey = lastEntry ? key(lastEntry) : 'no-data';

    return {
        articles,
        authors,
        categories,
        loading,
        loadingPage: !data?.[size - 1],
        loadNextPage,
        productResults,
        searchMode: baseState.mode,
        searchTerm: data?.[0]?.searchTerm,
        series,
        setFacets,
        setSize,
        totalSearchResults,
        dataKey,
    };
};
